/**
 * RecentRequests Component
 * Section displaying the latest 4 processing requests as cards
 */

import { useTranslation } from 'react-i18next';
import { RequestCard } from '@/components/dashboard/RequestCard';
import { useProcessingRequests } from '@/hooks/useAnalyses';
import { cn } from '@/lib/utils';
import type { ProcessingRequest } from '@/types/api';

export interface RecentRequestsProps {
  /** Additional class names */
  className?: string;
}

/**
 * Pad requests array to always have 3 items (fill with null for empty cards)
 */
function padRequests(requests: ProcessingRequest[]): (ProcessingRequest | null)[] {
  const padded: (ProcessingRequest | null)[] = [...requests];
  while (padded.length < 3) {
    padded.push(null);
  }
  return padded.slice(0, 3);
}

/**
 * Recent requests section for the dashboard
 */
export function RecentRequests({ className }: RecentRequestsProps) {
  const { t } = useTranslation();
  const { requests, isLoading } = useProcessingRequests();

  // Always show 3 cards, padded with empty cards if needed
  const displayRequests = padRequests(requests);

  return (
    <section className={cn('max-w-5xl mx-auto mt-10 px-4', className)}>
      {/* Section header */}
      <h2 className="text-xl font-bold text-[var(--color-charcoal)] mb-6">
        {t('requestCard.recentRequests')}:
      </h2>

      {/* Request cards grid - always 3 cards matching feature cards width */}
      <div
        className="grid gap-6 grid-cols-1 md:grid-cols-3"
        role="list"
        aria-label={t('requestCard.recentRequestsLabel')}
      >
        {isLoading ? (
          // Loading skeleton cards
          Array.from({ length: 3 }).map((_, index) => (
            <div
              key={`skeleton-${index}`}
              className="h-[140px] rounded-lg bg-gray-100 animate-pulse"
              role="listitem"
            />
          ))
        ) : (
          // Actual request cards (or empty placeholders)
          displayRequests.map((request, index) => (
            <div key={request?.id ?? `empty-${index}`} role="listitem">
              <RequestCard request={request} />
            </div>
          ))
        )}
      </div>
    </section>
  );
}
